import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { AbsoluteCenter, useToast } from '@chakra-ui/react';
import { motion, useAnimation } from 'framer-motion';
import { isEmpty } from 'lodash';
import { t } from 'i18next';
import { Button } from '@peacecover/component';
import SwipedBuddyPreference from '../../components/api/mutations/SwipedBuddyPreference';
import ToggleFollow from '../../components/api/mutations/ToggleFollow';
import useBuddyPreferenceQuery from '../../components/api/queries/useBuddyPreferenceQuery';
import { useAdMob } from '../chat/hooks/useAdMob';
import { useRestoreSwipe } from '@pocapan/shared/domains/findBuddy/ui/hooks/useRestoreSwipe';
import { useGetRecommendBuddyList } from '@pocapan/shared/domains/findBuddy/ui/hooks/useGetRecommendBuddyList';
import { openNewTab } from '@pocapan/shared/utils/UIUtils';
import { resolveDefaultRewardedUnitId } from '@pocapan/shared/utils/admob';
import RegularSolidButton from '../../components/buttons/RegularSolidButton';
import EmptyCase from '../../components/errors/EmptyCase';
import FollowedDialog from './components/FollowedDialog';
import StickyHeader from '../../components/StickyHeader';
import FanBuddyProfileCard from './components/FanBuddyProfileCard';
import ProfileAvatar from '../my-page/components/ProfileAvatar';
import CornerHolder from './components/CornerHolder';
import ToastMessageBox from '../../components/alerts/ToastMessageBox';
import { ReactComponent as ArrowReplayIcon } from '../../icons/arrow.reply.svg';
import { ReactComponent as ArrowTailIcon } from '../../icons/arrow.tail.svg';
import type { components } from '@pocapan/shared/types/schema';

type BuddyPreference = components['schemas']['ApiV2BuddyProfileCard'];

const FindFanBuddyCardPage = () => {
  const toast = useToast();
  const cardControls = useAnimation();
  const navigate = useNavigate();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isShowFollowedDialog, setIsShowFollowedDialog] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [swipedUserProfileIds, setSwipedUserProfileIds] = useState(new Set());
  const hasShownInterstitialAdRef = useRef(false);

  const currentUserBuddyPreference = useBuddyPreferenceQuery();
  const { mutateAsync: restoreSwipe } = useRestoreSwipe();

  const queryClient = useQueryClient();

  const adUnitId = resolveDefaultRewardedUnitId('interstitial');

  const { showAd } = useAdMob({
    adType: 'interstitial',
    featureKind: 'interstitial',
    autoLoad: true,
    onAdClosed: async () => {
      try {
        const response = await restoreSwipe({ adUnitId: adUnitId || '' });
        if (response.mbti) {
          hasShownInterstitialAdRef.current = true;
          queryClient.invalidateQueries(['MyBuddyPreferenceQuery']);
        }
      } catch (error) {
        console.error('[AdMob] interstitial error:', error);
      }
    },
    onError: (err) => {
      console.log('[AdMob] interstitial error:', err);
    }
  });

  const {
    data: recommendBuddyListData,
    fetchNextPage,
    hasNextPage,
    isLoading: isRecommendBuddyListLoading
  } = useGetRecommendBuddyList();
  const recommendBuddyList = recommendBuddyListData?.pages?.flatMap((page) =>
    page.recommendedBuddyProfileCards?.map((buddyPreference) => buddyPreference)
  );

  useEffect(() => {
    if (currentIndex % 7 === 0 && hasNextPage) {
      fetchNextPage();
    }
  }, [currentIndex, hasNextPage, fetchNextPage]);

  // 3초 후 로딩 화면 숨기기
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoading(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const buddyUserProfile = recommendBuddyList?.[currentIndex];

  // "3초 로딩" + "데이터 로딩" 중 더 늦게 끝나는 타이밍까지 로딩 화면 유지
  const isPageLoading =
    isInitialLoading || isRecommendBuddyListLoading || isEmpty(currentUserBuddyPreference);

  // 남은 스와이프가 0이 되는 시점에 전면형 광고 1회 노출 (중복 방지)
  useEffect(() => {
    if (isEmpty(currentUserBuddyPreference)) {
      return;
    }

    const remaining = currentUserBuddyPreference.remainingSwipeCount;

    if (!isInitialLoading && remaining <= 0 && !hasShownInterstitialAdRef.current) {
      hasShownInterstitialAdRef.current = true;
      showAd?.();
    }
  }, [isInitialLoading, currentUserBuddyPreference, showAd]);

  // 스와이프가 복구되어 remaining이 다시 양수가 되면 광고 노출 플래그를 리셋
  useEffect(() => {
    if (isEmpty(currentUserBuddyPreference)) {
      return;
    }
    const remaining = currentUserBuddyPreference.remainingSwipeCount;
    if (remaining > 0 && hasShownInterstitialAdRef.current) {
      hasShownInterstitialAdRef.current = false;
    }
  }, [currentUserBuddyPreference]);

  const hasPrevProfile = () => {
    return currentIndex !== 0;
  };

  const hasNextProfile = () => {
    return hasNextPage || currentIndex + 1 < (recommendBuddyList?.length || 0);
  };

  const presentRemainingCount = () => {
    return currentUserBuddyPreference.remainingSwipeCount >= 0;
  };

  const optimisticallyDecrementRemainingSwipeCount = () => {
    queryClient.setQueryData(['MyBuddyPreferenceQuery'], (prev: unknown) => {
      if (!prev || typeof prev !== 'object') return prev;
      const prevObj = prev as { remainingSwipeCount?: unknown };
      const remaining = prevObj.remainingSwipeCount;
      if (typeof remaining !== 'number') return prev;
      return {
        ...(prev as Record<string, unknown>),
        remainingSwipeCount: Math.max(0, remaining - 1)
      };
    });
  };

  const triggerSwipeAnimation = async (direction: 'left' | 'right') => {
    await cardControls.start({
      // 방향 반전: next(=right)일 때 왼쪽으로, prev(=left)일 때 오른쪽으로
      x: direction === 'right' ? -500 : 500,
      opacity: 0,
      rotate: direction === 'right' ? -50 : 50,
      transition: { duration: 0.3 }
    });
    cardControls.set({ x: 0, opacity: 1, rotate: 0 });
  };

  const swipeCardAnimation = async (direction: 'left' | 'right') => {
    await triggerSwipeAnimation(direction);
    setCurrentIndex((prev) => (direction === 'right' ? prev + 1 : Math.max(prev - 1, 0)));
  };

  const handlePrev = async () => {
    if (!hasPrevProfile()) return;

    const newIndex = Math.max(currentIndex - 1, 0);
    if (newIndex !== currentIndex) {
      swipeCardAnimation('left');
    }
  };

  const handleNext = async () => {
    if (!hasNextProfile() || !presentRemainingCount()) {
      return;
    }

    const userProfileId = buddyUserProfile?.userProfileId;
    if (!userProfileId) return;

    // 이미 스와이프한 프로필인지 확인
    if (swipedUserProfileIds.has(userProfileId)) {
      // API 호출 없이 바로 다음으로 이동
      swipeCardAnimation('right');
      return;
    }

    const response = await SwipedBuddyPreference(userProfileId);
    if (response?.ok) {
      // 스와이프한 프로필 ID 기록
      setSwipedUserProfileIds((prev) => new Set(prev).add(userProfileId));
      optimisticallyDecrementRemainingSwipeCount();
      queryClient.invalidateQueries(['MyBuddyPreferenceQuery']);
      swipeCardAnimation('right');
    } else {
      toast({
        duration: 1500,
        render: () => (
          <ToastMessageBox message={t('FindFanBuddyCardPage.nomoreSwipe')} showOnBottom={true} />
        )
      });
    }
  };

  const handleFollow = async () => {
    if (isEmpty(currentUserBuddyPreference) || isEmpty(buddyUserProfile)) {
      return;
    }

    if (buddyUserProfile.isMutualFollow) {
      setIsShowFollowedDialog(true);
      return;
    }

    const response = await ToggleFollow({
      isFollowed: false,
      currentUserProfileId: currentUserBuddyPreference.userProfileId,
      followUserProfileId: buddyUserProfile.userProfileId
    });

    if (response?.ok) {
      setIsShowFollowedDialog(true);
    }
  };

  const navigateBuddyUserProfile = (buddyPreference: BuddyPreference) => {
    if (buddyPreference?.userProfileId) {
      openNewTab(`/feed/user_profile/${buddyPreference?.userProfileId}`);
    }
  };

  const [dots, setDots] = useState('');

  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => {
        if (prev.length >= 3) {
          return '';
        }
        return prev + '.';
      });
    }, 500);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="safeAreaPaddingBottom h-[100vh] overflow-x-hidden overflow-y-hidden overscroll-y-none">
      <StickyHeader
        showBackButton={true}
        showTitle={true}
        alwaysShowTitle={true}
        title={t('FindFanBuddyCardPage.title') as string}
        transparent={true}
        rightContent={
          !isEmpty(currentUserBuddyPreference) ? (
            <ProfileAvatar
              thumbnailUrl={currentUserBuddyPreference.thumbnailUrl}
              widthAndHeight={'29px'}
              svgWidthAndHeight={'18px'}
              onClick={() =>
                openNewTab(`/feed/user_profile/${currentUserBuddyPreference?.userProfileId}`)
              }
            />
          ) : null
        }
      />
      <AbsoluteCenter
        style={{
          padding: '0px 20px 20px 24px',
          width: '100%'
        }}
      >
        {isPageLoading ? (
          <div className="flex flex-col items-center justify-center relative max-h-[589px] min-h-[589px]">
            <div className="flex flex-col items-center">
              <img
                src="/assets/icons/fan-buddy/LoadingBubble.png"
                alt="Loading"
                className="w-[152px] h-[152px] mb-[19px]"
              />
              <div className="flex flex-col items-center gap-[14px] py-[10px]">
                <p className="text-[20px] leading-[24px] font-semibold text-text-main-lv1 text-center">
                  {t('FindFanBuddyCardPage.loadingTitle')}
                </p>
                <p className="text-[15px] font-medium text-text-main-lv4 text-center">
                  {t('FindFanBuddyCardPage.loadingDescription')}
                  <span className="inline-block w-[30px] text-left">{dots}</span>
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div>
            {presentRemainingCount() ? (
              hasNextProfile() ? (
                <motion.div animate={cardControls} style={{ cursor: 'default' }}>
                  <div className="flex flex-col items-center relative ">
                    {buddyUserProfile && (
                      <FanBuddyProfileCard
                        size="extraLarge"
                        buddyProfileCard={buddyUserProfile}
                        isMine={false}
                        onCardClick={() => navigateBuddyUserProfile(buddyUserProfile)}
                        className="w-[335px] min-h-[517px]"
                      />
                    )}
                    <CornerHolder className="absolute top-[-12px] left-[-12px] z-[10]" />
                    <CornerHolder
                      className={`absolute right-[-12px] z-[10] rotate-180 ${buddyUserProfile?.gender === 'male' ? 'bottom-[24px]' : 'bottom-[-12px]'}`}
                    />
                    {buddyUserProfile?.gender === 'male' && (
                      <p className="mt-[18.5px] text-[13px] leading-[16px] font-semibold text-text-main-lv4">
                        {t('FindFanBuddyCardPage.isMaleUser')}
                      </p>
                    )}
                  </div>
                </motion.div>
              ) : (
                <div>
                  {/* @ts-ignore */}
                  <EmptyCase
                    emoji={'🥺'}
                    title={t('FindFanBuddyCardPage.emptyTitle')}
                    description={t('FindFanBuddyCardPage.emptyDescription')}
                    props={{ marginTop: '0px!important' }}
                  />
                </div>
              )
            ) : (
              <div>
                {/* @ts-ignore */}
                <EmptyCase
                  emoji={'🥺'}
                  title={t('FindFanBuddyCardPage.nomoreSwipe')}
                  description={t('FindFanBuddyCardPage.loadingDescription')}
                  props={{ marginTop: '0px!important' }}
                />
              </div>
            )}
          </div>
        )}
      </AbsoluteCenter>
      {/* Action Buttons */}
      {isPageLoading ? null : !isEmpty(recommendBuddyList) ? (
        <motion.div
          initial={{ opacity: 0.5 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div className="bottomNavbar flex gap-[20px] px-[24px] py-[12px] items-center !bg-transparent mb-[calc(env(safe-area-inset-top))]">
            <button
              type="button"
              onClick={handlePrev}
              className={`
                w-[52px] h-[52px] rounded-full flex items-center justify-center
                bg-pure-white dark:bg-pure-black
                shadow-[0_2px_1px_0_rgba(0,0,0,0.05),0_4px_4px_0_rgba(0,0,0,0.10)]
                dark:shadow-[0_2px_1px_0_rgba(0,0,0,0.20),0_4px_4px_0_rgba(0,0,0,0.40)]
              `}
              style={{
                filter:
                  'drop-shadow(0 0 8px rgba(0, 0, 0, 0.08)) drop-shadow(0 0 2px rgba(0, 0, 0, 0.05))'
              }}
            >
              <ArrowReplayIcon
                width={24}
                height={24}
                className={`${hasPrevProfile() ? 'fill-main-light-2' : 'fill-disabled-light'}`}
              />
            </button>
            <RegularSolidButton
              disabled={!hasNextProfile() || !presentRemainingCount()}
              text={t('FindFanBuddyCardPage.followButton')}
              onClicked={handleFollow}
              className={`
                !min-w-[183px] ${!hasNextProfile() || (!presentRemainingCount() && 'bg-disable text-on-disable')}
              `}
              style={{
                flexGrow: 1,
                height: '52px',
                fontSize: '17px',
                color: 'white',
                background: 'linear-gradient(180deg, #5C3DFA 0%, #754EEB 100%)',
                boxShadow:
                  '0px 2px 1px 0px rgba(0, 0, 0, 0.05), 0px 4px 4px 0px rgba(0, 0, 0, 0.10)'
              }}
            />
            {/* 다음 덕메로 이동버튼부분 */}
            <div className="relative">
              <button
                type="button"
                onClick={handleNext}
                className={`
                  w-[52px] h-[52px] rounded-full flex items-center justify-center
                  bg-pure-white dark:bg-pure-black
                  shadow-[0_2px_1px_0_rgba(0,0,0,0.05),0_4px_4px_0_rgba(0,0,0,0.10)]
                  dark:shadow-[0_2px_1px_0_rgba(0,0,0,0.20),0_4px_4px_0_rgba(0,0,0,0.40)]
                `}
                style={{
                  filter:
                    'drop-shadow(0 0 8px rgba(0, 0, 0, 0.08)) drop-shadow(0 0 2px rgba(0, 0, 0, 0.05))'
                }}
              >
                <ArrowTailIcon
                  width={24}
                  height={24}
                  className={`${hasNextProfile() ? 'fill-primary-light' : 'fill-disabled-light'}`}
                />
              </button>
              {hasNextProfile() && (
                <div
                  className="absolute rounded-full flex items-center justify-center"
                  style={{
                    top: -2,
                    right: -2,
                    width: '18px',
                    height: '18px',
                    background: 'linear-gradient(137.49deg, #FF5ACD 3.99%, #FF4C4C 100%)',
                    boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)'
                  }}
                >
                  <span className="text-white text-[13px]">
                    {Math.max(0, currentUserBuddyPreference.remainingSwipeCount)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      ) : (
        <div className="fixed bottom-0 pb-[calc(24px+env(safe-area-inset-bottom))] flex flex-col items-center gap-[20px] w-full px-[24px]">
          <Button
            size="large"
            className="w-full text-[17px] font-semibold"
            onClick={() => navigate('/fan-buddy/intro')}
          >
            {t('FindFanBuddyCardPage.recreateProfileCard')}
          </Button>
          <button
            type="button"
            className="text-[15px] leading-[18px] font-semibold text-text-main-lv2"
            onClick={() => navigate('/home')}
          >
            {t('FindFanBuddyCardPage.goToHome')}
          </button>
        </div>
      )}
      {isShowFollowedDialog && (
        <FollowedDialog
          isAlertOpen={isShowFollowedDialog}
          setIsAlertOpen={setIsShowFollowedDialog}
        />
      )}
    </div>
  );
};

export default FindFanBuddyCardPage;
